import { Helmet } from "react-helmet-async";

// Canonical production domain (confirmed by owner — no www).
export const SITE_URL = "https://atfwebworks.com";
export const SITE_NAME = "ATF WebWorks";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/atf-og.png`;

export function canonicalUrl(path = "/") {
  const p = (path || "/").startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${p === "/" ? "" : p}`;
}

// Per-page SEO head: title, description, canonical, Open Graph, Twitter cards, JSON-LD schema.
export function Seo({ title, description, path = "/", image, type = "website", noindex = false, jsonLd = [] }) {
  const url = canonicalUrl(path);
  const img = image || DEFAULT_OG_IMAGE;
  const schemas = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);
  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo192.png`,
  description:
    "ATF WebWorks helps you plan, build, launch, host, and grow your business with AI-powered strategy, professional website designs, and ongoing support.",
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
});

export const serviceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Website design, hosting & business growth",
  provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  areaServed: "US",
});

export const breadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: canonicalUrl(it.path),
  })),
});

export const productSchema = (d = {}) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: d.name,
  description: d.description,
  ...(d.image ? { image: [d.image] } : {}),
  ...(d.category ? { category: d.category } : {}),
  brand: { "@type": "Brand", name: SITE_NAME },
  offers: {
    "@type": "AggregateOffer",
    lowPrice: String(d.price || d.lowPrice || 997),
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url: canonicalUrl(`/template/${d.slug}`),
  },
});

export const faqSchema = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q || f.question,
    acceptedAnswer: { "@type": "Answer", text: f.a || f.answer },
  })),
});
